// Eskom PARCC — filter-bar primitives.
//
// `FilterBar` wraps the monolith's `.filters` row (label-above-control fields,
// wrapping). `SearchInput` is a thin controlled input styled by the existing
// `input` rules.
//
// `SelectFilter` delegates to the shared `Select`, which renders a native
// <select> under Legacy and the designed listbox under Lumen. That single
// delegation converts every facet dropdown in the app at once — FacetFilterBar
// (Dashboard / Transactions / Allocation), the findings filters and the working-
// paper filters — with no page-level change and no chance of the two themes
// offering different options.
import type { ReactNode } from 'react';
import { Select, type SelectOption } from './Select';

export type { SelectOption };

export function FilterBar({ children }: { children: ReactNode }): React.JSX.Element {
  return <div className="filters">{children}</div>;
}

export function SelectFilter({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: readonly SelectOption[] | readonly string[];
  onChange: (value: string) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const normalised: SelectOption[] = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  );
  return (
    <label>
      {label}
      {/* The visible <label> text is repeated as the control's accessible name:
          a <label> can name a native <select>, but not the Lumen <button>, so
          both themes must be given the name explicitly to stay identical. */}
      <Select
        value={value}
        options={normalised}
        onChange={onChange}
        disabled={disabled}
        ariaLabel={label}
      />
    </label>
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Optional field label; when set, wraps the input in a `.filters` label. */
  label?: string;
}): React.JSX.Element {
  const input = (
    <input
      type="search"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
    />
  );
  if (label === undefined) return input;
  return (
    <label>
      {label}
      {input}
    </label>
  );
}
